import React, { useState, useRef } from "react";
import { CloseIcon, EditIcon, TrashIcon } from "@/components/icons";
import {
  normalizeTag,
  parseHashtagInput,
  availableSuggestions,
} from "./websiteTagsEditor.logic";

export interface WebsiteTagObject {
  tag: string;
  properties: Record<string, string>;
}

interface WebsiteTagsEditorProps {
  tags: WebsiteTagObject[];
  onTagsChange: (tags: WebsiteTagObject[]) => void;
  isSaving: boolean;
  saveStatus: "idle" | "saving" | "saved";
  suggestions?: string[];
  /** Visual context. "dark" is for the review sidebar; "light" (default) for modals. */
  tone?: "light" | "dark";
}

export const WebsiteTagsEditor: React.FC<WebsiteTagsEditorProps> = ({
  tags,
  onTagsChange,
  isSaving,
  saveStatus,
  suggestions,
  tone = "light",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [expandedTag, setExpandedTag] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isDark = tone === "dark";
  // Text inputs render on either a white modal or the dark review sidebar, so
  // pin an explicit background + text colour per tone (defaults left the text
  // near-black on a dark panel, which was unreadable).
  const textInputClass = isDark
    ? "bg-gray-800 text-gray-100 placeholder:text-gray-500 border-gray-700 focus:ring-cyan-500"
    : "bg-white text-gray-900 placeholder:text-gray-400 border-gray-200 focus:ring-blue-400";

  const addTag = (raw: string) => {
    const normalized = normalizeTag(raw);
    if (!normalized) return;
    if (tags.some((t) => t.tag === normalized)) return;
    if (tags.length >= 20) return;
    onTagsChange([...tags, { tag: normalized, properties: {} }]);
    setInputValue("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseHashtagInput(e.target.value);
    if (parsed) {
      addTag(parsed.commit);
      setInputValue(parsed.nextInput);
      return;
    }
    setInputValue(e.target.value);
  };

  const removeTag = (tagName: string) => {
    onTagsChange(tags.filter((t) => t.tag !== tagName));
    if (expandedTag === tagName) setExpandedTag(null);
  };

  const toggleExpand = (tagName: string) => {
    setExpandedTag(expandedTag === tagName ? null : tagName);
  };

  const updateProperty = (
    tagName: string,
    oldKey: string,
    newKey: string,
    value: string
  ) => {
    onTagsChange(
      tags.map((t) => {
        if (t.tag !== tagName) return t;
        const props = { ...t.properties };
        if (oldKey !== newKey) delete props[oldKey];
        props[newKey] = value;
        return { ...t, properties: props };
      })
    );
  };

  const removeProperty = (tagName: string, key: string) => {
    onTagsChange(
      tags.map((t) => {
        if (t.tag !== tagName) return t;
        const props = { ...t.properties };
        delete props[key];
        return { ...t, properties: props };
      })
    );
  };

  const addProperty = (tagName: string) => {
    onTagsChange(
      tags.map((t) => {
        if (t.tag !== tagName) return t;
        const props = { ...t.properties };
        let newKey = "key";
        let i = 1;
        while (newKey in props) {
          newKey = `key${i++}`;
        }
        props[newKey] = "";
        return { ...t, properties: props };
      })
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag(inputValue);
    }
    if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      removeTag(tags[tags.length - 1].tag);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className={`text-sm font-medium ${isDark ? "text-gray-200" : "text-gray-700"}`}>
          Website Tags
        </label>
        {saveStatus === "saving" && (
          <span className="text-xs text-gray-400">Saving...</span>
        )}
        {saveStatus === "saved" && (
          <span className="text-xs text-green-500">Saved</span>
        )}
      </div>

      <p className="text-xs text-gray-400 mb-3">
        Tags tell your website where to place this image (e.g. hero, about,
        gallery). Click a tag to add placement properties.
      </p>

      <div className="flex flex-col gap-1.5 mb-2">
        {tags.map((tagObj) => {
          const isExpanded = expandedTag === tagObj.tag;
          const propCount = Object.keys(tagObj.properties).length;
          return (
            <div key={tagObj.tag}>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => toggleExpand(tagObj.tag)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full transition-colors ${
                    isExpanded
                      ? "bg-blue-100 text-blue-800"
                      : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                  }`}
                >
                  {tagObj.tag}
                  {propCount > 0 && (
                    <span className="text-blue-400 text-[10px]">
                      ({propCount})
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => removeTag(tagObj.tag)}
                  disabled={isSaving}
                  className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                >
                  <CloseIcon className="w-3 h-3" />
                </button>
              </div>

              {isExpanded && (
                <div className="ml-2 mt-1 mb-1 pl-2 border-l-2 border-blue-100">
                  {Object.entries(tagObj.properties).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-1 mb-1">
                      <input
                        type="text"
                        defaultValue={key}
                        onBlur={(e) => {
                          const newKey = e.target.value.trim();
                          if (newKey && newKey !== key) {
                            updateProperty(tagObj.tag, key, newKey, value);
                          }
                        }}
                        disabled={isSaving}
                        className={`w-24 px-1.5 py-0.5 text-xs border rounded-sm focus:outline-hidden focus:ring-1 disabled:opacity-50 ${textInputClass}`}
                        placeholder="key"
                      />
                      <span className="text-gray-300 text-xs">=</span>
                      <input
                        type="text"
                        defaultValue={value}
                        onBlur={(e) =>
                          updateProperty(
                            tagObj.tag,
                            key,
                            key,
                            e.target.value.trim()
                          )
                        }
                        disabled={isSaving}
                        className={`flex-1 px-1.5 py-0.5 text-xs border rounded-sm focus:outline-hidden focus:ring-1 disabled:opacity-50 ${textInputClass}`}
                        placeholder="value"
                      />
                      <button
                        type="button"
                        onClick={() => removeProperty(tagObj.tag, key)}
                        disabled={isSaving}
                        className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addProperty(tagObj.tag)}
                    disabled={isSaving}
                    className="text-xs text-blue-500 hover:text-blue-700 disabled:opacity-50 mt-0.5"
                  >
                    + Add property
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={
          tags.length >= 20 ? "Max 20 tags" : "Add tag and press Enter"
        }
        disabled={isSaving || tags.length >= 20}
        className={`w-full px-3 py-1.5 text-sm border rounded-md focus:outline-hidden focus:ring-1 disabled:opacity-50 ${isDark ? "disabled:bg-gray-900" : "disabled:bg-gray-50"} ${textInputClass}`}
      />

      {suggestions && suggestions.length > 0 && tags.length < 20 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {availableSuggestions(suggestions, tags).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => addTag(s)}
              disabled={isSaving}
              className="px-2 py-0.5 text-xs rounded-full bg-gray-100 hover:bg-blue-100 text-gray-600 hover:text-blue-700 border border-gray-200 disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
